import React, { useState, useEffect, useCallback } from 'react';

/**
 * ResizeHandle - Drag handle for resizable panels
 * 
 * KAI Standard: 700ms transitions, magnetic interaction
 * DEX Standard: Premium minimalism, subtle visual feedback
 * 
 * @param {function} onResize - Called with delta X during drag
 * @param {number} minWidth - Minimum width constraint (default 300)
 * @param {number} maxWidth - Maximum width constraint (default 600)
 * @param {string} position - 'left' or 'right' edge (default 'right')
 */
export default function ResizeHandle({ 
  onResize, 
  minWidth: _minWidth = 300, 
  maxWidth: _maxWidth = 600,
  position = 'right' 
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);
  
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e) => {
      // Invert delta for left-positioned panels
      const delta = position === 'left' ? -e.movementX : e.movementX;
      onResize(delta);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    
    // Add listeners to window to capture mouse outside component
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    // Change cursor globally while dragging
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, onResize, position]);
  
  return (
    <div
      className={`
        absolute ${position === 'right' ? 'right-0' : 'left-0'} top-0 bottom-0 
        w-1 cursor-col-resize z-10
        transition-all duration-200
        ${isDragging 
          ? 'bg-[#5a20ff] w-1' 
          : isHovered 
            ? 'bg-[#5a20ff]/30 w-1' 
            : 'bg-transparent hover:bg-[#5a20ff]/20'
        }
      `}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Visual grip indicator */}
      <div 
        className={`
          absolute top-1/2 -translate-y-1/2 
          ${position === 'right' ? '-right-1' : '-left-1'}
          w-3 h-8 flex flex-col justify-center items-center gap-0.5
          opacity-0 transition-opacity duration-200
          ${(isHovered || isDragging) ? 'opacity-100' : ''}
        `}
      >
        <div className="w-0.5 h-1 bg-[#5a20ff]/60 rounded-full" />
        <div className="w-0.5 h-1 bg-[#5a20ff]/60 rounded-full" />
        <div className="w-0.5 h-1 bg-[#5a20ff]/60 rounded-full" />
      </div>
    </div>
  );
}
